import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import {
  CardTypeCallToActions,
  Product,
  RouteTypes,
  SubscriptionProductIds,
  SubscriptionType,
} from '@app/enums/_index';
import { VoucherActivate, VoucherClaim, VoucherValidate } from '@app/interfaces/_index';
import {
  LMSSubscription,
  SearchObject,
  SubscriptionUIConfigurationItem,
  User,
} from '@app/models/_index';
import { GoogleAnalyticsService, UserService, VoucherService } from '@app/services/_index';
import {
  getDoceboSubscriptionNameProductName,
  getSubscriptionIdBySlug,
  getSubscriptionSlugByProductId,
} from '@app/utilities/subscription.utilities';

import { Step } from '@app/components/shared/steps/steps.interface';
import { SessionStorageKeys } from '@app/enums/_index';
import { SubscriptionUIConfigurationItemFactory } from '@app/factories/_index';
import { logGoogleAnalytics } from '@app/utilities/console-log.utilities';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { EnrollmentService } from '@app/services/_index';

@Component({
  selector: 'app-voucher-redemption',
  templateUrl: './voucher-redemption.component.html',
  styleUrls: ['./voucher-redemption.component.scss'],
  standalone: false,
})
export class VoucherRedemptionComponent implements OnInit {
  defaultErrorMessage = `An unexpected error has occurred.`;
  courses: SearchObject[] = [];
  voucherLookupForm: FormGroup;
  isLoading: boolean;
  firstStep = false;
  secondStep = false;
  isVoucherCode = false;
  voucherActivation = false;
  showHeroBannerButton = false;
  voucherCodeLimit = 6;
  showVoucherCodeError = false; // TODO: Show Voucher Code error response from service
  showVoucherActivateError = false; // TODO: Show error response from service
  disableActivateButton = false;
  currentUser: User; // Signed In User

  selectedSubscriptionUIConfiguration: SubscriptionUIConfigurationItem;
  selectedSubscription: LMSSubscription;
  showErrMsg: string;
  productName: string;
  productId: string;
  url: string;
  steps: Step[] = [
    {
      name: '1',
      title: 'VOUCHER_REDEMPTION.STEPS.VALIDATE',
      isActive: true,
    },
    {
      name: '2',
      title: 'VOUCHER_REDEMPTION.STEPS.ACTIVATE',
      isActive: false,
    },
    {
      name: '3',
      title: 'VOUCHER_REDEMPTION.STEPS.REDEEM',
      isActive: false,
    },
  ];

  validateResponse: VoucherValidate;
  beyondRegistrationURL = `${environment.beyondRegistration.url}/voucher-registration`;
  genPrimaryLinkClass = 'gen-primary-link';
  language: string = 'en';
  emailLink = 'mailto:education@genesys.com';

  constructor(
    private userService: UserService,
    private subscriptionUIItemFactory: SubscriptionUIConfigurationItemFactory,
    private formBuilder: FormBuilder,
    private voucherService: VoucherService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private router: Router,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private enrollmentService: EnrollmentService
  ) {
    translate.setDefaultLang('en');
    translate.use('en');
    this.voucherLookupForm = this.formBuilder.group({
      voucherCode: new FormControl(''),
    });
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.url = event.url;
      }
    });
  }

  async ngOnInit(): Promise<void> {
    // Supported languages : English (en), Spanish (es), Portuguese (pt), Japanese (ja)
    const SUPPORTED_LANGUAGES = ['en', 'es', 'pt', 'ja'] as const;
    type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
    // Handle language change from query parameter
    this.route.queryParams.subscribe((params) => {
      const lang = params['locale'];
      this.language = lang;
      if (lang && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
        this.translate.use(lang);
      }
    });

    this.currentUser = await firstValueFrom(this.userService?.doceboUser$);

    if (!this.currentUser) {
      this.showHeroBannerButton = true;
    }

    // TODO: Handle null or undefine search results
    this.courses = JSON.parse(
      sessionStorage.getItem(SessionStorageKeys.FULL_SEARCH_RESULTS)
    )?.searchresults;
  }

  get signInParams() {
    return {
      linkClass: this.genPrimaryLinkClass,
      url: this.beyondRegistrationURL,
      emailLink: this.emailLink,
    };
  }

  /**
   * Validates Voucher on Submission of Voucher code for a given subscription
   * @param submittedSubscription - The selected subscription in which the voucher will be redeemed
   * @param - track what Subscription will be redeemed
   */
  onVoucherSubmission(): void {
    const voucherCode = this.voucherLookupForm.value.voucherCode;
    this.isLoading = true;
    this.voucherService
      .getVoucherClaim(voucherCode)
      .then((response: VoucherValidate[]) => {
        const voucherClaim = response.filter(
          (item) => item.status == 'reserved' && item.user == this.currentUser?.email.toLowerCase()
        );
        if (voucherClaim.length > 0) {
          this.firstStep = true;
          this.steps[0].isActive = false;
          this.steps[1].isActive = true;
          this.validateResponse = voucherClaim[0];
          this.productId = voucherClaim[0].productId;
          this.productName = getDoceboSubscriptionNameProductName(this.productId);
          this.isLoading = false;
        } else {
          const translateLanguage = this.language ? this.language : 'en';
          // Validate Voucher process
          this.voucherService
            .claimVoucher(voucherCode, translateLanguage)
            .then((response: VoucherClaim) => {
              this.validateResponse = response;
              this.productId = this.validateResponse?.productId;
              this.productName = getDoceboSubscriptionNameProductName(this.productId);
              this.firstStep = true;
              this.steps[0].isActive = false;
              this.steps[1].isActive = true;
              this.isLoading = false;
            })
            .catch((error) => {
              const parseErrorObject = JSON.parse(JSON.stringify(error));
              this.showErrMsg = parseErrorObject?.error
                ? parseErrorObject?.error?.message
                : this.defaultErrorMessage;
              this.showVoucherCodeError = true;
              this.isLoading = false;
            });
        }
      })
      .catch((error) => {
        const parseErrorObject = JSON.parse(JSON.stringify(error));
        this.showErrMsg = parseErrorObject?.error
          ? parseErrorObject?.error?.message
          : this.defaultErrorMessage;
        this.showVoucherCodeError = true;
        this.isLoading = false;
      });
  }

  /**
   * Redeem Voucher for valid Voucher code
   */
  redeemVoucher(): void {
    this.isLoading = true;
    // Activate Voucher Process
    if (this.validateResponse && this.currentUser) {
      // On the finalise redeem voucher passing the language based on query string on the url else the default English (en) will be passed.
      const translateLanguage = this.language ? this.language : 'en';
      this.disableActivateButton = true;
      this.voucherService
        .reedemVoucher(
          this.validateResponse?.voucherId,
          this.validateResponse?.claimId,
          this.currentUser.id,
          this.productId,
          translateLanguage
        )
        .then(async (_) => {
          // Re-enroll user in any expired/archived courses from the newly activated subscription
          if (this.currentUser?.id && this.courses?.length) {
            const subscriptionSlug = getSubscriptionSlugByProductId(this.productId);
            const subscriptionId = getSubscriptionIdBySlug(subscriptionSlug);
            if (subscriptionId !== -1) {
              const subscriptionCourseIds = this.courses
                .filter((course) => course.parentId === subscriptionId)
                .map((course) => course.id);
              await this.enrollmentService.reEnrollExpiredSubscriptionCourses(
                this.currentUser.id,
                subscriptionCourseIds
              );
            }
          }
          this.firstStep = false;
          this.secondStep = true;
          this.steps[1].isActive = false;
          this.steps[2].isActive = true;
          this.isLoading = false;
          if (
            this.productId &&
            this.productId !== SubscriptionProductIds.AGENT_PLATFORM_AP_TRAINING_FOR_GENESYS_CLOUD
          ) {
            this.selectedSubscriptionUIConfiguration =
              this.subscriptionUIItemFactory.getSubscriptionUIConfigurationItem(
                getSubscriptionSlugByProductId(this.productId)
              );
          }
        })
        .catch((error) => {
          const parseErrorObject = JSON.parse(JSON.stringify(error));
          this.showErrMsg = parseErrorObject?.error
            ? parseErrorObject?.error?.message
            : this.defaultErrorMessage;
          this.showVoucherActivateError = true;
          this.isLoading = false;
        });
    }
  }

  /**
   * Determines whether to show the explore section based on product type and current step.
   *
   * For restricted products (AGENT_PLATFORM_AP_TRAINING_FOR_GENESYS_CLOUD and
   * PARTNER_RESELL_AGENT_PLATFORM_AP_SUBSCRIPTION):
   * - Shows in Step 1 (Validate)
   * - Shows in Step 2 (Activate)
   * - Hides in Step 3 (Redeem)
   *
   * For all other products:
   * - Shows in all steps
   *
   * @returns {boolean} True if the explore section should be displayed, false otherwise
   */
  get shouldShowExploreSection(): boolean {
    const isAPProduct =
      this.productId &&
      (this.productId ===
        this.subscriptionProductIds.AGENT_PLATFORM_AP_TRAINING_FOR_GENESYS_CLOUD ||
        this.productId ===
          this.subscriptionProductIds.PARTNER_RESELL_AGENT_PLATFORM_AP_SUBSCRIPTION);

    if (isAPProduct) {
      return !this.secondStep;
    }

    return true;
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * Validates voucher code entered as input
   * @param voucherCode - Voucher code as input for validation.
   */
  onChange(voucherCode: string): void {
    if (voucherCode && voucherCode.length >= this.voucherCodeLimit) {
      this.isVoucherCode = true;
    }
  }

  /**
   *  CardTypeCallToActions enums based on card types
   */
  public get cardTypeCallToActions(): typeof CardTypeCallToActions {
    return CardTypeCallToActions;
  }

  /**
   *  RouteTypes enums based on route types
   */
  public get routeTypes(): typeof RouteTypes {
    return RouteTypes;
  }

  /**
   * TODO: Refactor to Subscription Util
   * Return query string to be passed to the search route to filter the catalog
   * by subscription product
   * product = search or product to search
   * @returns search query string based on subscription
   */
  getSearchQuery(subscriptionNameSlug: string): any {
    switch (subscriptionNameSlug) {
      case SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION:
        return { product: Product.GENESYS_ENGAGE };
      case SubscriptionType.PURECONNECT_SUBSCRIPTION:
        return { product: Product.PURE_CONNECT };
      case SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION:
        return { product: Product.GENESYS_CLOUD };
      case SubscriptionType.POINTILLIST_SUBSCRIPTION:
        return { product: Product.POINTILLIST };
      default:
        return '';
    }
  }

  /**
   * Return boolean value to determine whether the routing navigation can happen or not
   * @returns boolean value based on the condition
   */
  canDeactivate(): boolean {
    if (this.firstStep) {
      if (document.getElementById('voucherModal')) {
        document.getElementById('voucherModal').style.display = 'block';
      }
      return false;
    } else {
      return true;
    }
  }

  /**
   * Reset back to step 1 of the Voucher Redemption and navigates back to the url
   */
  reset(): void {
    if (document.getElementById('voucherModal')) {
      document.getElementById('voucherModal').style.display = 'none';
    }
    this.router.navigate([this.url]);
    this.steps[0].isActive = true;
    this.steps[1].isActive = false;
    this.firstStep = false;
    this.secondStep = false;
    this.voucherLookupForm.get('voucherCode').reset();
  }

  /**
   * Cancel current navigation and user remains on the step 2
   */
  cancel(): void {
    if (document.getElementById('voucherModal')) {
      document.getElementById('voucherModal').style.display = 'none';
    }
  }

  /**
   * Subscription Product Ids enums based on Product Ids
   */
  public get subscriptionProductIds(): typeof SubscriptionProductIds {
    return SubscriptionProductIds;
  }
}
